import React, { useMemo, useState, useEffect } from "react";
import { Input, Select, Spin, Table, Dropdown } from "antd";
import { LuFilter } from "react-icons/lu";
import {
  CloseOutlined,
  EditOutlined,
  MoreOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { Search } from "lucide-react";
import SelectAssignmentModal from "../../../components/Modals/SelectAssignmentModal";
import imageOne from "../../../assets/images/Image (1).png";
import imageTwo from "../../../assets/images/Image (2).png";
import { InterpreterIcon } from "../../../assets/svgs/icons";
import {
  calculateInterpreterStats,
  mapInterpretersToTableData,
} from "./helpers";
import AddInterpreterModal from "../../../components/Modals/AddInterpreterModal";
import { ACTIONS_DROPDOWN_PROPS } from "../../../constants/actionsDropdown";
import { buildConditionalPagination } from "../../../utils/pagination";
import {
  useGetInterpretersQuery,
  useGetInterpreterStatsQuery,
} from "../../../store/api/apiSlice";
import { debounce } from "lodash";

const assignments = [
  {
    thumbnail: imageTwo,
    duration: "15:43",
    organizationLogo: imageOne,
    organizationName: "Roku, Inc.",
    campaign: "Yearly Campaign",
    survey: "July Survey",
    interpreterName: "Mary Freund",
    interpreterEmail: "david20@gmail.com",
    completedDate: "Aug 22, 2024",
    status: "Completed",
    interpreterImage: imageOne,
  },
  // Other assignment objects...
];

const Interpreter = () => {
  // Define separate state for search input field and the actual search query
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({
    status: undefined,
    sort: undefined,
  });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingInterpreter, setEditingInterpreter] = useState(null);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedInterpreter, setSelectedInterpreter] = useState(null);

  // Query params as a dependency for the query
  const queryParams = useMemo(() => {
    const params = {
      search: searchText,
    };

    // Only add defined filters
    if (filters.status) {
      params.status = filters.status;
    }

    if (filters.sort) {
      params.sort = filters.sort;
    }

    return params;
  }, [searchText, filters.status, filters.sort]);

  // Queries - pass the queryParams object
  const {
    data: interpreters,
    isLoading,
    refetch,
  } = useGetInterpretersQuery(queryParams);

  const { data: stats } = useGetInterpreterStatsQuery();
  const overviewQueryParams = useMemo(() => ({ search: "" }), []);
  const { data: overviewInterpreters } =
    useGetInterpretersQuery(overviewQueryParams);

  // Refetch when query params change
  useEffect(() => {
    refetch();
  }, [queryParams, refetch]);

  const calculatedStats = useMemo(
    () => calculateInterpreterStats(overviewInterpreters, stats),
    [overviewInterpreters, stats],
  );

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchText(value);
      }, 500),
    [],
  );

  // Handle search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value); // Update the input field value
    debouncedSearch(value); // Debounce the actual query
  };

  // Clear search
  const clearSearch = () => {
    setSearchInput(""); // Clear the input field
    setSearchText(""); // Clear the search query
  };

  // Handle filter changes
  const handleFilterChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setFilterVisible(false);
    // The filters are applied automatically through the queryParams
    refetch();
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: undefined,
      sort: undefined,
    });
    setFilterVisible(false);
  };

  // Handle edit
  const handleEdit = (record) => {
    const editData = {
      _id: record._id,
      status: record.status,
      interpreter: record.interpreter,
      experience: record.experience,
    };
    setEditingInterpreter(editData);
    setEditModalVisible(true);
  };

  const handleAssign = (record) => {
    setSelectedInterpreter(record);
    setAssignModalVisible(true);
  };

  const getActionsMenuItems = () => [
    { key: "edit", label: "Edit", icon: <EditOutlined /> },
    { key: "assign", label: "Assign", icon: <UserAddOutlined /> },
  ];

  const handleActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "edit":
        handleEdit(record);
        break;
      case "assign":
        handleAssign(record);
        break;
      default:
        break;
    }
  };

  const getStatusDisplayText = (status) => {
    if (status === "not_active" || status === "deactivate") {
      return "Inactive";
    }

    return String(status || "").replace(/_/g, " ");
  };

  const columns = [
    {
      title: "Sr#",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Interpreter",
      key: "interpreter",
      render: (_, record) => {
        const avatarUrl = record.interpreter.avatar;
        return (
        <div className="flex items-center gap-3">
          <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-300 bg-gradient-to-br from-gray-50 to-gray-500">
            {!avatarUrl && <InterpreterIcon />}
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={record.interpreter.name}
                className="h-full w-full object-contain p-1.5 drop-shadow-sm"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : null}
          </div>
          <div>
            <div className="font-medium text-gray-900">
              {record.interpreter.name}
            </div>
            <div className="text-sm text-gray-500">
              {record.interpreter.email}
            </div>
          </div>
        </div>
        );
      },
    },
    {
      title: "Assignments",
      key: "assignments",
      render: (_, record) => {
        return (
          <span>
            {record.completedAssignments}/{record.totalAssignments}
          </span>
        );
      },
    },

    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <span
          className={`inline-flex px-3 py-1 rounded-full text-sm capitalize ${
            record.status === "active"
              ? "bg-yellow text-green-800"
              : "bg-red-500 text-white"
          }`}
        >
          {getStatusDisplayText(record.status)}
        </span>
      ),
    },
    {
      title: "Joined Date",
      dataIndex: "joinedDate",
      key: "joinedDate",
    },
    {
      title: "",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            {...ACTIONS_DROPDOWN_PROPS}
            menu={{
              items: getActionsMenuItems(record),
              onClick: (event) =>
                handleActionMenuClick(record, event.key, event),
            }}
          >
            <button
              type="button"
              className="actions-dots-btn"
              onClick={(e) => e.stopPropagation()}
              aria-label={`Open actions for ${
                record?.interpreter?.name || "interpreter"
              }`}
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];

  const tableData = useMemo(
    () => mapInterpretersToTableData(interpreters),
    [interpreters],
  );

  const tablePagination = buildConditionalPagination(tableData);

  const StatCard = ({ value, label, subtext }) => (
    <div className="bg-secondary rounded-xl p-3 shadow-sm">
      <div className="flex flex-col">
        <div className="text-2xl font-semibold text-gray-900">{value}</div>
        <div className="text-gray-500 text-sm">{label}</div>
        {subtext && <div className="text-xs text-gray-400">{subtext}</div>}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6 min-h-screen">
      <div className="flex justify-between items-center selectSecondary">
        <h3 className="text-md font-semibold m-0">Overview</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard value={calculatedStats.total} label="Total Interpreters" />
        <StatCard value={calculatedStats.active} label="Active Interpreters" />
        <StatCard
          value={calculatedStats.completed}
          label="Completed Assignments"
        />
        <StatCard
          value={calculatedStats.incomplete}
          label="Incomplete Assignments"
        />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1">
          <Input
            placeholder="Search..."
            value={searchInput}
            prefix={<Search className="text-md" />}
            suffix={
              searchInput && (
                <CloseOutlined
                  className="text-md cursor-pointer hover:text-gray-600"
                  onClick={clearSearch}
                />
              )
            }
            onChange={handleSearchChange}
            className=" h-11 rounded-2xl"
          />
        </div>
        <Select
          value={filters.sort}
          placeholder="Sort by"
          className="md:w-40 h-11"
          onChange={(value) => handleFilterChange("sort", value)}
          allowClear
          options={[
            { value: "newest", label: "Newest" },
            { value: "oldest", label: "Oldest" },
          ]}
        />
        <Select
          value={filters.status}
          placeholder="Status"
          className="w-[76%] md:w-40 h-11"
          onChange={(value) => handleFilterChange("status", value)}
          allowClear
          options={[
            {
              value: "assigned",
              label: "Assigned",
            },
            {
              value: "unassigned",
              label: "Unassigned",
            },
            { value: "active", label: "Active" },
            {
              value: "deactivate",
              label: "Deactivate",
            },
          ]}
        />
        <button
          className={`h-11 w-11 flex items-center justify-center rounded-lg ${
            filterVisible ? "bg-primary text-white" : "bg-secondary"
          } border border-gray-200 borderred`}
          onClick={() => {
            if (filterVisible) {
              applyFilters();
            } else {
              setFilterVisible(true);
            }
          }}
        >
          <LuFilter
            size={20}
            className={filterVisible ? "text-white" : "text-red-500"}
          />
        </button>
      </div>
      <div className="bg-white rounded-xl overflow-hidden">
        <Table
          rowKey="_id"
          loading={isLoading}
          columns={columns}
          dataSource={tableData}
          pagination={tablePagination}
          className="custom-table"
          rowClassName="hover:bg-gray-50"
          scroll={{ x: 800 }}
        />
      </div>
      <AddInterpreterModal
        visible={editModalVisible}
        onClose={() => {
          setEditModalVisible(false);
          setEditingInterpreter(null);
        }}
        editData={editingInterpreter}
        onSuccess={() => {
          refetch();
        }}
      />

      <SelectAssignmentModal
        visible={assignModalVisible}
        onClose={() => setAssignModalVisible(false)}
        interpreter={selectedInterpreter}
        assignments={assignments}
        onSubmit={(selectedIds) => {
          setAssignModalVisible(false);
          refetch();
        }}
      />
    </div>
  );
};

export default Interpreter;
